import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  Check,
  X,
  Plus,
  Filter,
  Shield,
  AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AlertRecord {
  id: number;
  agencyId: number;
  internalId: string | null;
  lastName: string | null;
  firstName: string | null;
  reason: string | null;
  createdBy: number | null;
  createdAt: string;
  isRead: boolean;
  severity: string;
  alertType: string;
}

interface ProtectionOrderAlert {
  id: number;
  personId: number;
  orderNumber: string | null;
  expirationDate: string;
  restrainedParty: string | null;
  protectedParties: string | null;
  personFirstName: string | null;
  personLastName: string | null;
  daysUntilExpiration: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const severityColors: Record<string, string> = {
  info: "bg-blue-100 text-blue-700",
  warning: "bg-amber-100 text-amber-700",
  critical: "bg-red-100 text-red-700",
};

const alertTypeLabels: Record<string, string> = {
  proximity: "Proximity",
  new_contact: "New Contact",
  protection_order_expiring: "PO Expiring",
  custom: "Custom",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AlertsPage() {
  const queryClient = useQueryClient();
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [readFilter, setReadFilter] = useState<string>("all");
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { data: alertsData, isLoading: alertsLoading } = useQuery<{
    data: AlertRecord[];
  }>({
    queryKey: ["alerts", "all"],
    queryFn: () => api.get<{ data: AlertRecord[] }>("/alerts"),
  });

  const { data: poAlertsData, isLoading: poLoading } = useQuery<{
    data: ProtectionOrderAlert[];
  }>({
    queryKey: ["alerts", "protection-orders"],
    queryFn: () =>
      api.get<{ data: ProtectionOrderAlert[] }>(
        "/alerts/protection-orders",
      ),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => api.put(`/alerts/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/alerts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  const createAlertMutation = useMutation({
    mutationFn: (data: {
      firstName?: string;
      lastName?: string;
      reason: string;
      severity?: string;
      alertType?: string;
    }) => api.post("/alerts", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      setShowCreateForm(false);
    },
  });

  const alerts = alertsData?.data ?? [];
  const poAlerts = poAlertsData?.data ?? [];

  // Apply filters
  const filteredAlerts = alerts.filter((a) => {
    if (severityFilter !== "all" && a.severity !== severityFilter) return false;
    if (readFilter === "unread" && a.isRead) return false;
    if (readFilter === "read" && !a.isRead) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Alerts</h1>
          <p className="text-sm text-muted-foreground">
            Manage abuser alerts and protection order notifications.
          </p>
        </div>
        <Button
          className="gap-2"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          <Plus className="h-4 w-4" />
          New Alert
        </Button>
      </div>

      {/* Create alert form */}
      {showCreateForm && (
        <Card>
          <CardContent className="p-4">
            <CreateAlertForm
              onSubmit={(data) => createAlertMutation.mutate(data)}
              onCancel={() => setShowCreateForm(false)}
              isLoading={createAlertMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {/* Expiring Protection Orders */}
      {!poLoading && poAlerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-4 w-4 text-amber-600" />
              Expiring Protection Orders ({poAlerts.length})
            </CardTitle>
            <CardDescription>
              Protection orders expiring within the next 30 days.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Order #
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Expires
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Days Left
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Restrained Party
                  </th>
                </tr>
              </thead>
              <tbody>
                {poAlerts.map((po) => (
                  <tr key={po.id} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">
                      {po.personFirstName} {po.personLastName}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {po.orderNumber ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(po.expirationDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                          po.daysUntilExpiration <= 7
                            ? "bg-red-100 text-red-700"
                            : po.daysUntilExpiration <= 14
                              ? "bg-amber-100 text-amber-700"
                              : "bg-blue-100 text-blue-700",
                        )}
                      >
                        {po.daysUntilExpiration} day
                        {po.daysUntilExpiration !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {po.restrainedParty ?? "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filters:</span>
        </div>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        >
          <option value="all">All Severities</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </select>
        <select
          value={readFilter}
          onChange={(e) => setReadFilter(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        >
          <option value="all">All</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
        </select>
      </div>

      {/* Alerts table */}
      {alertsLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {!alertsLoading && filteredAlerts.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Bell className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No alerts found.
            </p>
          </CardContent>
        </Card>
      )}

      {!alertsLoading && filteredAlerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Alerts ({filteredAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Severity
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Reason
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAlerts.map((alert) => (
                  <tr
                    key={alert.id}
                    className={cn(
                      "border-b last:border-0",
                      !alert.isRead && "bg-accent/20",
                    )}
                  >
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                          severityColors[alert.severity] ??
                            severityColors.warning,
                        )}
                      >
                        {alert.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {alertTypeLabels[alert.alertType] ?? alert.alertType}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {alert.firstName || alert.lastName
                        ? `${alert.firstName ?? ""} ${alert.lastName ?? ""}`.trim()
                        : "-"}
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-muted-foreground">
                      {alert.reason ?? "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {new Date(alert.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                          alert.isRead
                            ? "bg-gray-100 text-gray-600"
                            : "bg-blue-100 text-blue-700",
                        )}
                      >
                        {alert.isRead ? "Read" : "Unread"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {!alert.isRead && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              markReadMutation.mutate(alert.id)
                            }
                            title="Mark as read"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            dismissMutation.mutate(alert.id)
                          }
                          title="Dismiss"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create alert form
// ---------------------------------------------------------------------------

interface CreateAlertFormProps {
  onSubmit: (data: {
    firstName?: string;
    lastName?: string;
    reason: string;
    severity?: string;
    alertType?: string;
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function CreateAlertForm({
  onSubmit,
  onCancel,
  isLoading,
}: CreateAlertFormProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [reason, setReason] = useState("");
  const [severity, setSeverity] = useState("warning");
  const [alertType, setAlertType] = useState("custom");

  return (
    <div className="space-y-3">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="h-4 w-4" />
        Create New Alert
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            First Name
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Optional"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Last Name
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Optional"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Reason</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Alert reason / description..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Severity
          </label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Type</label>
          <select
            value={alertType}
            onChange={(e) => setAlertType(e.target.value)}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="custom">Custom</option>
            <option value="proximity">Proximity</option>
            <option value="new_contact">New Contact</option>
            <option value="protection_order_expiring">
              PO Expiring
            </option>
          </select>
        </div>
      </div>
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() =>
            onSubmit({
              firstName: firstName || undefined,
              lastName: lastName || undefined,
              reason,
              severity,
              alertType,
            })
          }
          loading={isLoading}
          disabled={!reason.trim()}
        >
          Create Alert
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
